import type { ReactNode } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  TriangleAlert,
  X,
} from 'lucide-react';
import { cn } from '../lib/cn';
import { IconButton } from './IconButton';

/** Semantic toast tones — maps product success/error/warning/info. */
export type ToastTone = 'default' | 'success' | 'warning' | 'danger';

const toneClass: Record<ToastTone, string> = {
  default: 'border-ds-brand/30 bg-ds-brand-subtle',
  success: 'border-ds-success/25 bg-ds-success-subtle',
  warning: 'border-ds-warning/30 bg-ds-warning-subtle',
  danger: 'border-ds-danger/25 bg-ds-danger-subtle',
};

const iconClass: Record<ToastTone, string> = {
  default: 'text-ds-brand',
  success: 'text-ds-success',
  warning: 'text-ds-warning',
  danger: 'text-ds-danger',
};

const icons: Record<ToastTone, typeof Info> = {
  default: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: AlertCircle,
};

export type ToastProps = {
  title: string;
  description?: string;
  tone?: ToastTone;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
};

/**
 * Presentational toast surface. Hosts should manage a queue + portal.
 * Solid surface + soft semantic fill (not glass).
 */
export function Toast({
  title,
  description,
  tone = 'default',
  action,
  onDismiss,
  className,
}: ToastProps) {
  const Icon = icons[tone];

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        // Wider than w-80 so multi-field validation lists stay readable.
        'flex w-[min(28rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] items-start gap-2 rounded-ds-lg border p-3 text-ds-text shadow-ds-menu',
        toneClass[tone],
        className,
      )}
    >
      <Icon
        className={cn('mt-0.5 size-4 shrink-0', iconClass[tone])}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="whitespace-pre-line text-ds-sm font-medium text-ds-text">
          {title}
        </div>
        {description ? (
          <p className="mt-0.5 whitespace-pre-line text-ds-xs text-ds-muted">
            {description}
          </p>
        ) : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
      {onDismiss ? (
        <IconButton
          label="Dismiss"
          onClick={onDismiss}
          className="-mt-0.5 -mr-1"
        >
          <X />
        </IconButton>
      ) : null}
    </div>
  );
}
